export const ROUTES = {
  root: '/',
  overview: '/overview',
  todos: '/todos',
  todoApproval: '/todos/approval',
  todoBusiness: '/todos/business',
  callback: '/callback',
  portalSite: '/portal-site',
  user: {
    login: '/user/login',
    accessDenied: '/user/access-denied',
  },
  park: {
    setup: '/park/setup',
    create: '/park/create',
    select: '/park/select',
    error: '/park/error',
    profile: '/park/profile',
    spaceManagement: '/park/space-management',
    spaces: '/park/space-management/spaces',
    otherAssets: '/park/space-management/other-asset',
    spaceIncubation: '/park/space-management/incubation',
    spacesOverview: '/park/space-management/overview',
    spaceDetail: '/park/space-management/spaces/:uuid',
    spaceUsage: '/park/space-management/usage',
    leases: '/park/leases',
    property: '/property',
    workOrders: '/property/work-orders',
    accessControl: '/property/access-control',
    parking: '/property/parking',
    meetingRoom: '/property/meeting-room',
    energy: '/property/energy',
    energySpaces: '/property/energy/spaces',
    energyMeters: '/property/energy/meters',
    energyLedger: '/property/energy/ledger',
    energyAccounts: '/property/energy/accounts',
    energyRates: '/property/energy/rates',
    iotDevices: '/property/iot-devices',
  },
  asset: {
    root: '/asset',
    ledger: '/asset/ledger',
    ledgerDetail: '/asset/ledger/:uuid',
    suppliers: '/asset/suppliers',
    reminders: '/asset/reminders',
  },
  contracts: {
    types: '/contract/type',
    management: '/contract',
    all: '/contract/all',
    mine: '/contract/mine',
    todo: '/contract/todo',
    templates: '/contract/templates',
    billingRules: '/contract/billing-rules',
  },
  attraction: {
    leads: '/attraction/lead',
    leadAll: '/attraction/lead/all',
    leadMine: '/attraction/lead/mine',
    leadPool: '/attraction/lead/pool',
    customers: '/attraction/customer',
    customerAll: '/attraction/customer/all',
    customerMine: '/attraction/customer/mine',
    customerPool: '/attraction/customer/pool',
    contacts: '/attraction/contact',
    opportunities: '/attraction/opportunity',
    orders: '/attraction/order',
    setting: '/attraction/setting',
    claimConversion: '/attraction/setting/claim-conversion',
  },
  enterprises: {
    root: '/enterprises',
    attraction: '/attraction',
    list: '/attraction/list',
    libraryDetail: '/attraction/list/:parkEnterpriseUuid',
    libraryClaim: '/attraction/list/:parkEnterpriseUuid/claim',
    investmentProjects: '/attraction/investment-projects',
    admissionApplications: '/attraction/admission-applications',
    exits: '/enterprises/exits',
    visits: '/enterprises/visits',
    requests: '/enterprises/requests',
    incubation: '/enterprises/incubation',
    incubationPlans: '/enterprises/incubation/plans',
    mentors: '/enterprises/incubation/mentors',
    archives: '/enterprises/archives',
    qualifications: '/enterprises/archives/qualifications',
    intellectualProperties: '/enterprises/archives/intellectual-properties',
    annualData: '/enterprises/archives/annual-data',
  },
  growth: {
    root: '/growth',
    models: '/growth/models',
    archives: '/growth/archives',
    evaluations: '/growth/evaluations',
    recommendationRules: '/growth/recommendation-rules',
  },
  services: {
    handling: '/services/handling',
    matters: '/services/handling/matters',
    applications: '/services/handling/applications',
    marketplace: '/services/marketplace',
    demands: '/services/marketplace/demands',
    catalog: '/services/marketplace/catalog',
    providers: '/services/marketplace/providers',
    orders: '/services/marketplace/orders',
    fulfillments: '/services/marketplace/fulfillments',
    reviews: '/services/marketplace/reviews',
  },
  resources: {
    investment: '/resources/investment',
    technology: '/resources/technology',
    technologyResults: '/resources/technology/results',
    researchInstitutions: '/resources/technology/research-institutions',
    experts: '/resources/technology/experts',
    technologyManagers: '/resources/technology/managers',
    ecosystem: '/resources/ecosystem',
  },
  operations: {
    activities: '/operations/activities',
    activityOps: '/operations/activity-ops',
    policies: '/operations/policies',
    announcements: '/operations/announcements',
    content: '/operations/content',
    partyBuilding: '/operations/party-building',
  },
  hr: {
    root: '/hr',
  },
  finance: {
    root: '/finance',
    overview: '/finance/overview',
    receivables: '/finance/receivables',
    receipts: '/finance/receipts',
    collectionLedger: '/finance/collection-ledger',
    expenses: '/finance/expenses',
    invoices: '/finance/invoices',
  },
  reports: {
    root: '/reports',
    overview: '/reports/overview',
    investment: '/reports/investment',
    finance: '/reports/finance',
    property: '/reports/property',
    settings: '/reports/settings',
  },
  evaluation: {
    overview: '/evaluation/overview',
    indicatorSystems: '/evaluation/indicator-systems',
    dataCollections: '/evaluation/data-collections',
    evidence: '/evaluation/evidence',
    tasks: '/evaluation/tasks',
    reports: '/evaluation/reports',
  },
  portal: {
    overview: '/portal/overview',
    site: '/portal/site',
    pages: '/portal/site/pages',
    columns: '/portal/site/columns',
    contents: '/portal/site/contents',
    banners: '/portal/site/banners',
    publish: '/portal/publish',
    releases: '/portal/publish/releases',
    settings: '/portal/publish/settings',
  },
  settings: {
    root: '/settings',
    spaceConfig: '/settings/space-config',
    permissions: '/settings/permissions',
    approvalConfig: '/settings/approval-config',
    dictionaries: '/settings/dictionaries',
    numberingRules: '/settings/numbering-rules',
    auditLogs: '/settings/audit-logs',
  },
} as const;

export const isPublicPortalPath = (pathname: string) =>
  pathname === ROUTES.portalSite ||
  pathname.startsWith(`${ROUTES.portalSite}/`);

export const portalSiteHomePath = (parkUuid: string) =>
  `${ROUTES.portalSite}/${parkUuid}`;

export const portalSiteContentsPath = (parkUuid: string) =>
  `${portalSiteHomePath(parkUuid)}/contents`;

export const portalSiteContentDetailPath = (parkUuid: string, uuid: string) =>
  `${portalSiteContentsPath(parkUuid)}/${uuid}`;

export const portalSitePoliciesPath = (parkUuid: string) =>
  `${portalSiteHomePath(parkUuid)}/policies`;

export const portalSitePolicyDetailPath = (parkUuid: string, uuid: string) =>
  `${portalSitePoliciesPath(parkUuid)}/${uuid}`;

export const portalSiteActivitiesPath = (parkUuid: string) =>
  `${portalSiteHomePath(parkUuid)}/activities`;

export const portalSiteActivityDetailPath = (parkUuid: string, uuid: string) =>
  `${portalSiteActivitiesPath(parkUuid)}/${uuid}`;

export const portalSiteAnnouncementsPath = (parkUuid: string) =>
  `${portalSiteHomePath(parkUuid)}/announcements`;

export const portalSiteAnnouncementDetailPath = (
  parkUuid: string,
  uuid: string,
) => `${portalSiteAnnouncementsPath(parkUuid)}/${uuid}`;

export const PARK_BOOTSTRAP_PATHS = [
  ROUTES.park.setup,
  ROUTES.park.select,
  ROUTES.park.error,
] as const;

export const enterpriseDetailPath = (uuid: string) =>
  `${ROUTES.enterprises.root}/${uuid}`;

export const enterpriseLibraryDetailPath = (parkEnterpriseUuid: string) =>
  `${ROUTES.enterprises.list}/${parkEnterpriseUuid}`;

export const enterpriseExitDetailPath = (uuid: string) =>
  `${ROUTES.enterprises.exits}/${uuid}`;

export const enterpriseLibraryClaimPath = (parkEnterpriseUuid: string) =>
  `${ROUTES.enterprises.list}/${parkEnterpriseUuid}/claim`;

export const spaceDetailPath = (uuid: string) =>
  `${ROUTES.park.spaces}/${uuid}`;

export const policyDetailPath = (uuid: string) =>
  `${ROUTES.operations.policies}/${uuid}`;

export const announcementNewPath = () =>
  `${ROUTES.operations.announcements}/new`;

export const announcementDetailPath = (uuid: string) =>
  `${ROUTES.operations.announcements}/${uuid}`;

export const announcementEditPath = (uuid: string) =>
  `${ROUTES.operations.announcements}/${uuid}/edit`;

export const workOrderDetailPath = (uuid: string) =>
  `${ROUTES.park.workOrders}/${uuid}`;

export const announcementReadsPath = (uuid: string) =>
  `${ROUTES.operations.announcements}/${uuid}/reads`;

export const serviceMatterNewPath = () => `${ROUTES.services.matters}/new`;

export const serviceMatterDetailPath = (uuid: string) =>
  `${ROUTES.services.matters}/${uuid}`;

export const serviceMatterEditPath = (uuid: string) =>
  `${ROUTES.services.matters}/${uuid}/edit`;

export const serviceApplicationDetailPath = (uuid: string) =>
  `${ROUTES.services.applications}/${uuid}`;

export const portalContentNewPath = () => `${ROUTES.portal.contents}/new`;

export const portalContentDetailPath = (uuid: string) =>
  `${ROUTES.portal.contents}/${uuid}`;

export const portalContentEditPath = (uuid: string) =>
  `${ROUTES.portal.contents}/${uuid}/edit`;

export const operationsContentNewPath = () =>
  `${ROUTES.operations.content}/new`;

export const operationsContentDetailPath = (uuid: string) =>
  `${ROUTES.operations.content}/${uuid}`;

export const operationsContentEditPath = (uuid: string) =>
  `${ROUTES.operations.content}/${uuid}/edit`;

export const portalColumnsPath = (pageUuid?: string) =>
  withQuery(ROUTES.portal.columns, { pageUuid });

export const withQuery = (
  path: string,
  query: Record<string, string | number | undefined | null>,
) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  const search = params.toString();
  return search ? `${path}?${search}` : path;
};
